import type { TreeDetail } from '@api/types'
import { SpeciesSheet } from '@features/learn/SpeciesSheet'
import { ReportForm } from '@features/report/ReportForm'
import { useI18n } from '@i18n/I18nProvider'
import { formatDate } from '@i18n/locale'
import { Button } from '@ui/Button'
import { ConditionBadge } from '@ui/ConditionBadge'
import { Icon } from '@ui/Icon'
import { Photo } from '@ui/Photo'
import { useState } from 'react'
import type { Channel } from './channel'

// El contenido de la ficha de un árbol: foto, nombres, estado, el aviso (paso a paso), dónde está
// y la especie en una hoja encima. Lo usan la pantalla del QR (TreePage) y la hoja que se abre
// desde el mapa (TreeSheet): allí el nombre va en el título de la hoja (`showName` false) y el
// aviso ya está en la tarjeta de debajo (`withReport` false).
export const TreeDetails = ({
	tree,
	channel,
	showName = true,
	withReport = true,
}: {
	tree: TreeDetail
	channel: Channel
	showName?: boolean
	withReport?: boolean
}) => {
	const { t, locale } = useI18n()
	const [speciesOpen, setSpeciesOpen] = useState(false)
	const name = tree.species?.commonName ?? t('tree.code', { code: tree.code })
	return (
		<>
			{tree.species?.photoUrl && (
				<Photo
					src={tree.species.photoUrl}
					alt={tree.species.commonName}
					className="mb-4 aspect-[4/3] w-full rounded-3xl object-cover"
				/>
			)}
			<p className="font-semibold text-earth-800">{t('tree.code', { code: tree.code })}</p>
			{showName && <h1 className="mb-1 text-3xl leading-tight">{name}</h1>}
			{tree.species && <p className="mb-4 text-earth-800 italic">{tree.species.scientificName}</p>}

			<ConditionBadge latest={tree.latestCondition} />

			{withReport && (
				<section className="my-6 rounded-3xl bg-white p-5 shadow-sm">
					<h2 className="text-xl font-bold">{t('tree.howIsIt')}</h2>
					<p className="mt-1 mb-4 text-earth-800">{t('tree.howIsItHint')}</p>
					<ReportForm code={tree.code} channel={channel} stepwise legend={t('tree.howIsIt')} hideLegend />
				</section>
			)}

			<dl className={`grid gap-3 ${withReport ? '' : 'mt-6'}`}>
				{tree.municipality && (
					<div>
						<dt className="text-sm text-earth-800">{t('tree.municipality')}</dt>
						<dd className="font-semibold">{tree.municipality.name}</dd>
					</div>
				)}
				{tree.stage && (
					<div>
						<dt className="text-sm text-earth-800">{t('tree.stage')}</dt>
						<dd className="font-semibold">{tree.stage.name}</dd>
					</div>
				)}
				{tree.plantedAt && <dd>{t('tree.plantedAt', { date: formatDate(tree.plantedAt, locale) })}</dd>}
				{tree.locationSource === 'planting_data' && (
					<dd className="flex items-start gap-2 text-sm text-earth-800">
						<Icon name="pin" className="size-5 shrink-0" />
						{t('tree.approximateLocation')}
					</dd>
				)}
			</dl>

			{tree.species && (
				<div className="mt-6">
					<Button variant="secondary" fullWidth aria-haspopup="dialog" onClick={() => setSpeciesOpen(true)}>
						<Icon name="leaf" />
						{t('tree.aboutSpecies')}
					</Button>
				</div>
			)}
			{tree.species && speciesOpen && (
				<SpeciesSheet
					id={tree.species.id}
					regionId={tree.region?.id ?? null}
					onClose={() => setSpeciesOpen(false)}
				/>
			)}
		</>
	)
}
